import { useParams } from "react-router-dom";
import { Printer } from "lucide-react";
import { useApi } from "@/pos/lib/api";
import { rm, qty, dt, dateOnly, ringgitWords, METHOD_LABEL } from "@/pos/lib/format";

function useDoc() {
  const { id } = useParams();
  const { data: sale } = useApi(`/sales/${id}`);
  const { data: shop } = useApi("/settings");
  return { sale, shop };
}

const PrintBar = ({ label }) => (
  <div className="no-print sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white/90 px-4 py-3 backdrop-blur">
    <span className="text-sm font-semibold">{label}</span>
    <button onClick={() => window.print()} className="btn-primary" data-testid="print-now-btn"><Printer size={16} /> Cetak</button>
  </div>
);

export function Receipt() {
  const { sale: s, shop } = useDoc();
  if (!s || !shop) return <div className="p-6 text-slate-500">Memuatkan...</div>;
  return (
    <div className="min-h-screen bg-slate-100 print:bg-white">
      <style>{"@page { size: 80mm auto; margin: 3mm; }"}</style>
      <PrintBar label="Resit 80mm" />
      <div className="receipt mx-auto my-6 w-[302px] bg-white p-4 font-mono text-[12px] leading-snug text-black shadow print:my-0 print:shadow-none" data-testid="thermal-receipt">
        <div className="text-center">
          <img src="/img/logo.png" alt="Logo" className="mx-auto mb-1 h-24 w-24 object-contain" data-testid="receipt-logo" />
          <div className="text-[15px] font-bold">{shop.shop_name}</div>
          {shop.ssm && <div>({shop.ssm})</div>}
          <div>{shop.address}</div>
          <div>Tel: {shop.phone}</div>
        </div>
        <div className="my-2 border-t border-dashed border-black" />
        <div>No: {s.number}</div>
        <div>Tarikh: {dt(s.created_at)}</div>
        <div>Juruwang: {s.cashier}</div>
        {s.customer_name && <div>Pelanggan: {s.customer_name}</div>}
        {s.status === "batal" && <div className="mt-1 text-center font-bold">*** DIBATALKAN ***</div>}
        <div className="my-2 border-t border-dashed border-black" />
        {s.items.map((i, k) => (
          <div key={k} className="mb-1">
            <div>{i.name}</div>
            <div className="flex justify-between"><span>{qty(i.qty, i.unit)} x {i.price.toFixed(2)}</span><span>{i.subtotal.toFixed(2)}</span></div>
          </div>
        ))}
        <div className="my-2 border-t border-dashed border-black" />
        <div className="flex justify-between"><span>Subjumlah</span><span>{s.subtotal.toFixed(2)}</span></div>
        {s.discount > 0 && <div className="flex justify-between"><span>Diskaun</span><span>-{s.discount.toFixed(2)}</span></div>}
        {s.rounding !== 0 && <div className="flex justify-between"><span>Pembundaran</span><span>{s.rounding.toFixed(2)}</span></div>}
        <div className="flex justify-between text-[15px] font-bold"><span>JUMLAH</span><span data-testid="receipt-total">{rm(s.total)}</span></div>
        <div className="flex justify-between"><span>{METHOD_LABEL[s.payment_method]}</span><span>{s.amount_received.toFixed(2)}</span></div>
        {s.payment_method === "tunai" && <div className="flex justify-between"><span>Baki</span><span>{s.change.toFixed(2)}</span></div>}
        <div className="my-2 border-t border-dashed border-black" />
        <div className="text-center">{shop.footer}</div>
        <div className="mt-2 text-center tracking-[0.3em]">||| {s.number} |||</div>
      </div>
    </div>
  );
}

export function Invoice() {
  const { sale: s, shop } = useDoc();
  if (!s || !shop) return <div className="p-6 text-slate-500">Memuatkan...</div>;
  return (
    <div className="min-h-screen bg-slate-100 print:bg-white">
      <style>{"@page { size: A4; margin: 14mm; }"}</style>
      <PrintBar label="Invois / Bil A4" />
      <div className="mx-auto my-6 w-full max-w-[794px] bg-white p-6 text-[13px] text-slate-900 shadow sm:p-10 print:my-0 print:p-0 print:shadow-none" data-testid="a4-invoice">
        <div className="flex flex-col justify-between gap-4 border-b-2 border-slate-900 pb-5 sm:flex-row">
          <div className="flex items-start gap-4">
          <img src="/img/logo.png" alt="Logo" className="h-20 w-20 object-contain" data-testid="invoice-logo" />
          <div>
            <div className="font-heading text-2xl font-extrabold">{shop.shop_name}</div>
            {shop.ssm && <div className="text-slate-500">No. Pendaftaran: {shop.ssm}</div>}
            <div className="max-w-xs text-slate-600">{shop.address}</div>
            <div className="text-slate-600">Tel: {shop.phone}</div>
          </div>
          </div>
          <div className="sm:text-right">
            <div className="font-heading text-3xl font-extrabold tracking-tight text-red-700">INVOIS</div>
            <div className="font-mono">{s.number}</div>
            <div className="text-slate-500">Tarikh: {dateOnly(s.created_at)}</div>
            {s.status === "batal" && <div className="font-bold text-rose-600">DIBATALKAN</div>}
          </div>
        </div>
        <div className="mt-5 grid grid-cols-2 gap-4">
          <div><div className="text-xs uppercase tracking-wider text-slate-500">Kepada</div><div className="font-semibold">{s.customer_name || "Pelanggan Tunai"}</div></div>
          <div className="text-right"><div className="text-xs uppercase tracking-wider text-slate-500">Status Bayaran</div><div className="font-semibold text-red-700">DIBAYAR · {METHOD_LABEL[s.payment_method]}</div></div>
        </div>
        <table className="mt-6 w-full border-collapse">
          <thead><tr className="bg-slate-900 text-left text-white"><th className="p-2">#</th><th className="p-2">Perkara</th><th className="p-2 text-right">Kuantiti</th><th className="p-2 text-right">Harga Seunit (RM)</th><th className="p-2 text-right">Jumlah (RM)</th></tr></thead>
          <tbody>{s.items.map((i, k) => (
            <tr key={k} className="border-b border-slate-200"><td className="p-2">{k + 1}</td><td className="p-2">{i.name}</td><td className="p-2 text-right font-mono">{qty(i.qty, i.unit)}</td><td className="p-2 text-right font-mono">{i.price.toFixed(2)}</td><td className="p-2 text-right font-mono">{i.subtotal.toFixed(2)}</td></tr>
          ))}</tbody>
        </table>
        <div className="mt-4 flex justify-end">
          <div className="w-64 space-y-1 font-mono">
            <div className="flex justify-between"><span>Subjumlah</span><span>{s.subtotal.toFixed(2)}</span></div>
            {s.discount > 0 && <div className="flex justify-between"><span>Diskaun</span><span>-{s.discount.toFixed(2)}</span></div>}
            {s.rounding !== 0 && <div className="flex justify-between"><span>Pembundaran</span><span>{s.rounding.toFixed(2)}</span></div>}
            <div className="flex justify-between border-t-2 border-slate-900 pt-1 text-base font-bold"><span>JUMLAH</span><span data-testid="invoice-total">{rm(s.total)}</span></div>
          </div>
        </div>
        <div className="mt-4 rounded bg-slate-50 p-3 text-sm italic" data-testid="invoice-words">{ringgitWords(s.total)}</div>
        <div className="mt-16 grid grid-cols-2 gap-10">
          <div className="border-t border-slate-400 pt-2 text-center text-xs text-slate-500">Disediakan oleh: {s.cashier}</div>
          <div className="border-t border-slate-400 pt-2 text-center text-xs text-slate-500">Tandatangan Pelanggan</div>
        </div>
        <div className="mt-8 text-center text-xs text-slate-500">{shop.footer}</div>
      </div>
    </div>
  );
}
